import React, { useState } from 'react';
import theme from './SensitiveInfoConfirmDialog.module.scss';
import { getProjectSensitiveViewerMembers } from '@/services/projects';
import { ProjectMember } from '@/types';
import { Trans } from '@lingui/react/macro';
import { UserIcon, ProfileIcon } from '@/components/Icons';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { Modal, ModalHeader, ModalBody, ModalFooter, Checkbox, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@/components/Buttons';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';

interface SensitiveInfoDialogProps {
    profileId: number;
    show: boolean;
    onAccept(doNotShowAgain: boolean): void;
    onClose(): void;
    onCancel?(): void;
}

const SensitiveInfoConfirmDialog: React.FC<SensitiveInfoDialogProps> = ({
    profileId,
    show,
    onAccept,
    onClose,
    onCancel,
}) => {
    const projectId = useSelector((state: IState) => state.global.activeProject?.id);

    const [members, setMembers] = useState<ProjectMember[] | null>(null);
    const [doNotShowAgain, setDoNotShowAgain] = useState(false);

    const handleCancelClick = () => {
        triggerHandler(onCancel);
        triggerHandler(onClose);
    };

    const handleOpen = async () => {
        setDoNotShowAgain(false);
        setMembers(null);

        var result = await getProjectSensitiveViewerMembers(projectId!, profileId);
        setMembers(result);
    };

    const handleAcceptClick = async () => {
        triggerHandler(onAccept, doNotShowAgain);
        triggerHandler(onClose);
    };

    const renderContent = () => {
        if (!members) {
            return <LoadingIndicator />;
        }

        return (
            <div className={theme.container}>
                <div className={theme.section}>
                    <Trans>
                        Please note, marking a document as sensitive hides the document from other users who are not allowed
                        to view sensitive content. The buyer of the unit will still be able to view the document.
                    </Trans>
                </div>

                <div className={theme.section}>
                    <Trans>Those users will be able to view this document:</Trans>

                    <ul className={theme.userList}>
                        {members.map((m) => (
                            <li key={`${m.profileId}_${m.userId}`}>
                                <ProfileIcon className={theme.avatar} profileId={m.profileId} sizeInPx={32} />
                                <UserIcon className={theme.avatar} userId={m.userId} sizeInPx={32} />
                                <div className={theme.name}>
                                    {m.userName}
                                    <small>{m.profileName}</small>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className={theme.section}>
                    <Checkbox
                        inputSize="small"
                        checked={doNotShowAgain}
                        onChange={(e) => setDoNotShowAgain(e.target.checked)}>
                        <Trans>Do not show this message again for this project</Trans>
                    </Checkbox>
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen}>
            <ModalHeader>
                <Trans>Mark as sensitive</Trans>
            </ModalHeader>
            <ModalBody>{renderContent()}</ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button type="submit" color="primary" onClick={handleAcceptClick}>
                    <Trans>Mark document as sensitive</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default SensitiveInfoConfirmDialog;
